import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /translate/naati — NAATI certified translation hub (TH)
import { createFileRoute } from "@tanstack/react-router";
import { CertTypeHub } from "@/components/translate/CertTypeHub";
import { getCertType } from "@/data/translation-certtypes";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema, serviceWithReviewsSchema, howToSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { naatiOfferCatalog, naatiServiceSchema, naatiSpeakableSchema, naatiPersonGraph } from "@/lib/naati-schema";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_TH } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";
const cert = getCertType("naati")!;

export const Route = createFileRoute("/translate/naati/")({
  loader: async () => ({ clusterFaq: await buildClusterFaqJsonLdAsync("naati", "th", "https://nyclanguageinstitute.com/translate/naati") }),
  head: ({ loaderData }: any) => {
    const url = `${SITE}/translate/naati`;
    const title = `${cert.nameTh} | NYC Language Institute`;
    const desc = cert.taglineTh;
    return {
      meta: [
        { title: ctaTitle(title, "/translate/naati", "th") }, { name: "description", content: ctaDesc(desc, "/translate/naati", "th")},
        { name: "keywords", content: cert.keywords.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/translate/naati", "th")}, { property: "og:description", content: ctaDesc(desc, "/translate/naati", "th")},
        { property: "og:url", content: url },
      ],
      links: canonicalAndHreflang("/translate/naati", "/en/translate/naati"),
      scripts: [
        speakableSchema(),
        naatiSpeakableSchema(url),
        naatiOfferCatalog({ url, inLanguage: "th" }),
        naatiServiceSchema({ url, name: title, description: desc, inLanguage: "th" }),
        naatiPersonGraph(),
        howToSchema({
          name: "ขั้นตอนสั่งแปลรับรอง NAATI กับ NYC",
          description: cert.taglineTh,
          totalTime: "PT3D", estimatedCost: { currency: "THB", value: String(cert.priceFromThb) },
          steps: cert.stepsTh.map((s) => ({ name: s.name, text: s.text })),
        }),
        consolidateGraph([
          serviceWithReviewsSchema({ path: "/translate/naati", serviceType: "NAATI Certified Translation", name: title, description: desc, priceFromThb: cert.priceFromThb }),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "FAQPage", "@id": `${url}#faq`, inLanguage: "th",
              mainEntity: cert.faqsTh.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "แปลรายภาษา", item: `${SITE}/translate` },
                { "@type": "ListItem", position: 3, name: "NAATI", item: url },
              ],
            }),
          },
        ]),
        entitySameAsSchema(),
        softwareApplicationSchema({
          url: `${SITE}/api/public/mcp`,
          name: "NYC Language Institute — MCP Server",
          description: "AI agent endpoint exposing NAATI Certified Translation tooling for ChatGPT / Claude / Perplexity.",
        }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_TH(url)),
      
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
    };
  },
  component: () => <CertTypeHub cert={cert} locale="th" />,
});
